"use client";

import { ChevronsRightIcon } from "../lifestyleIcons";

interface ResultsCtaProps {
  count: number;
  canReset: boolean;
  /** Category noun for the dirty CTA, e.g. "Cars" → "VIEW 58 CARS". */
  categoryLabel?: string | null;
  onReset: () => void;
  onApply: () => void;
}

/**
 * The sticky footer.
 *
 * Clean state is a single orange "N RESULTS »" button. Once anything is
 * selected, Figma swaps in RESET (orange outline) beside "VIEW N {CATEGORY}".
 * Filters apply live, so the primary button only reports the running count and
 * dismisses the drawer — it is a way out, not an Apply.
 */
export default function ResultsCta({ count, canReset, categoryLabel, onReset, onApply }: ResultsCtaProps) {
  const noun = categoryLabel?.toUpperCase() || "RESULTS";
  const primaryLabel = canReset ? `View ${count} ${noun}` : `${count} results`;

  return (
    <div className="flex items-center shrink-0 w-full pt-2 pb-4 lg:pb-8 px-4 gap-3 bg-white drop-shadow-[0px_0px_2px_rgba(0,0,0,0.25)]">
      {canReset && (
        <button
          type="button"
          onClick={onReset}
          className="flex items-center justify-center flex-1 min-w-0 h-10 px-3 text-sm font-medium uppercase text-dark-1 bg-transparent border border-primary rounded transition-colors hover:bg-orange-2">
          Reset
        </button>
      )}

      <button
        type="button"
        onClick={onApply}
        disabled={count === 0}
        className={`flex items-center justify-center h-10 px-3 gap-1.5 text-sm font-medium uppercase text-white bg-primary rounded transition-colors hover:bg-primary/90 disabled:opacity-38 disabled:cursor-not-allowed ${
          canReset ? "shrink-0 w-[220px]" : "flex-1 min-w-0 w-full"
        }`}>
        <span className="truncate">{primaryLabel}</span>
        <ChevronsRightIcon className="shrink-0 w-4 h-4" />
      </button>
    </div>
  );
}
